import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './ReportPage.api';
import { MODULI_REPORT, MODULO_REPORT_LABEL, BADGE_MODULO_REPORT } from '../../config/moduliReport';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface SmtpOption { id: number; tipo: string; fromName: string; fromEmail: string }
interface SocietaSorgente {
  id: number;
  nome: string;
  ragioneSociale: string | null;
  attiva: boolean;
  numTemplate: number;
}
interface TemplateSorgente {
  id: number;
  nome: string;
  descrizione: string | null;
  moduloGestionale: string;
  attivo: boolean;
  updatedAt: string;
  dimensioneHbs: number;
  haSmtp: boolean;
}
interface EsitoImport {
  id: number;
  nome: string;
  nomeOriginale: string;
  rinominato: boolean;
  sorgenteSocieta: string;
  smtpDaConfigurare: boolean;
}
interface ReportTemplate {
  id: number;
  societaId: number;
  nome: string;
  descrizione: string | null;
  moduloGestionale: string;
  contenutoHbs: string;
  cssOverride: string | null;
  smtpId: number | null;
  smtp: SmtpOption | null;
  mittenteNomeOverride: string | null;
  mittenteEmailOverride: string | null;
  oggettoEmailDefault: string | null;
  corpoEmailDefault: string | null;
  attivo: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function ReportPage() {
  const { societaId, setSocietaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const canEdit = can('impostazioni.report', 'edit');

  // ── Lista ──
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroTesto, setFiltroTesto] = useState('');
  const [filtroModulo, setFiltroModulo] = useState('');
  const [filtroStato, setFiltroStato] = useState('');

  // ── Modale template ──
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [moduloGestionale, setModuloGestionale] = useState('GENERICO');
  const [cssOverride, setCssOverride] = useState('');
  const [smtpId, setSmtpId] = useState('');
  const [mittenteNomeOverride, setMittenteNomeOverride] = useState('');
  const [mittenteEmailOverride, setMittenteEmailOverride] = useState('');
  const [oggettoEmailDefault, setOggettoEmailDefault] = useState('');
  const [corpoEmailDefault, setCorpoEmailDefault] = useState('');
  const [hbsFile, setHbsFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [smtpOptions, setSmtpOptions] = useState<SmtpOption[]>([]);
  const hbsInputRef = useRef<HTMLInputElement>(null);

  // ── Modale duplica ──
  const [duplicaModalOpen, setDuplicaModalOpen] = useState(false);
  const [duplicaTemplateId, setDuplicaTemplateId] = useState<number | null>(null);
  const [duplicaNome, setDuplicaNome] = useState('');
  const [duplicando, setDuplicando] = useState(false);

  // ── Modale importa da altra società ──
  const [importOpen, setImportOpen] = useState(false);
  const [sorgenti, setSorgenti] = useState<SocietaSorgente[]>([]);
  const [sorgenteId, setSorgenteId] = useState('');
  const [templateSorgente, setTemplateSorgente] = useState<TemplateSorgente[]>([]);
  const [loadingSorgente, setLoadingSorgente] = useState(false);
  const [selezionati, setSelezionati] = useState<number[]>([]);
  const [filtroImport, setFiltroImport] = useState('');
  const [importando, setImportando] = useState(false);
  const [importErrore, setImportErrore] = useState('');
  const [importEsito, setImportEsito] = useState<EsitoImport[] | null>(null);

  // ── Caricamento lista ──
  const caricaLista = useCallback(async () => {
    if (!societaId) return;
    setLoading(true);
    try {
      // @api GET /api/report-templates?societaId= -> {"success":true,"data":[...]}
      const r = await apiFetch<{ success: boolean; data: ReportTemplate[] }>(`/api/report-templates?societaId=${societaId}`);
      setTemplates(r.data || []);
    } catch { /* errore silenzioso */ } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => { if (pronta && societaId) caricaLista(); }, [pronta, societaId, caricaLista]);

  // ── Carica SMTP per modale ──
  const caricaSmtp = useCallback(async () => {
    try {
      // @api GET /api/smtp?tipo=EMAIL -> {"smtp":[...]}
      const email = await apiFetch<{ smtp: SmtpOption[] }>('/api/smtp?tipo=EMAIL');
      // @api GET /api/smtp?tipo=PEC -> {"smtp":[...]}
      const pec = await apiFetch<{ smtp: SmtpOption[] }>('/api/smtp?tipo=PEC');
      setSmtpOptions([...(email.smtp || []), ...(pec.smtp || [])]);
    } catch { /* ignora */ }
  }, []);

  // ── Filtro client-side ──
  const filtered = templates.filter(t => {
    if (filtroTesto && !t.nome.toLowerCase().includes(filtroTesto.toLowerCase())) return false;
    if (filtroModulo && t.moduloGestionale !== filtroModulo) return false;
    if (filtroStato === 'attivo' && !t.attivo) return false;
    if (filtroStato === 'inattivo' && t.attivo) return false;
    return true;
  });

  // ── Azioni ──
  const apriCrea = () => {
    setEditId(null);
    setNome(''); setDescrizione(''); setModuloGestionale('GENERICO');
    setCssOverride(''); setSmtpId('');
    setMittenteNomeOverride(''); setMittenteEmailOverride('');
    setOggettoEmailDefault(''); setCorpoEmailDefault('');
    setHbsFile(null);
    caricaSmtp();
    setModalOpen(true);
  };

  const apriModifica = (t: ReportTemplate) => {
    setEditId(t.id);
    setNome(t.nome); setDescrizione(t.descrizione || ''); setModuloGestionale(t.moduloGestionale);
    setCssOverride(t.cssOverride || ''); setSmtpId(t.smtpId ? String(t.smtpId) : '');
    setMittenteNomeOverride(t.mittenteNomeOverride || '');
    setMittenteEmailOverride(t.mittenteEmailOverride || '');
    setOggettoEmailDefault(t.oggettoEmailDefault || '');
    setCorpoEmailDefault(t.corpoEmailDefault || '');
    setHbsFile(null);
    caricaSmtp();
    setModalOpen(true);
  };

  const salvaTemplate = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if (!editId) fd.append('societaId', societaId);
      fd.append('nome', nome.trim());
      fd.append('descrizione', descrizione.trim());
      fd.append('moduloGestionale', moduloGestionale);
      fd.append('cssOverride', cssOverride.trim());
      if (smtpId) fd.append('smtpId', smtpId);
      fd.append('mittenteNomeOverride', mittenteNomeOverride.trim());
      fd.append('mittenteEmailOverride', mittenteEmailOverride.trim());
      fd.append('oggettoEmailDefault', oggettoEmailDefault.trim());
      fd.append('corpoEmailDefault', corpoEmailDefault.trim());
      if (hbsFile) fd.append('file', hbsFile);

      if (editId) {
        // @api PUT /api/report-templates/:id FormData{nome,moduloGestionale,...} -> {"success":true}
        await apiFetch(`/api/report-templates/${editId}`, { method: 'PUT', body: fd });
      } else {
        if (!hbsFile) { setSaving(false); return; }
        // @api POST /api/report-templates FormData{societaId,nome,moduloGestionale,file,...} -> {"success":true,"data":{"id":"number"}}
        const result = await apiFetch<{ success: boolean; data: { id: number } }>('/api/report-templates', { method: 'POST', body: fd });
        setModalOpen(false);
        window.open(`/impostazioni/report/${result.data.id}/editor`, '_blank');
        caricaLista();
        return;
      }
      setModalOpen(false);
      caricaLista();
    } catch { /* errore */ } finally {
      setSaving(false);
    }
  };

  const apriDuplica = (t: ReportTemplate) => {
    setDuplicaTemplateId(t.id);
    setDuplicaNome(`Copia di ${t.nome}`);
    setDuplicaModalOpen(true);
  };

  const eseguiDuplica = async () => {
    if (!duplicaTemplateId || !duplicaNome.trim()) return;
    setDuplicando(true);
    try {
      // @api POST /api/report-templates/:id/duplica {"societaId":"number","nome":"string"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch(`/api/report-templates/${duplicaTemplateId}/duplica`, {
        method: 'POST',
        body: JSON.stringify({ societaId: parseInt(societaId), nome: duplicaNome.trim() }),
      });
      setDuplicaModalOpen(false);
      caricaLista();
    } catch { /* errore */ } finally {
      setDuplicando(false);
    }
  };

  // ── Import da altra società ────────────────────────────────────────────
  const apriImport = async () => {
    setImportOpen(true);
    setSorgenteId('');
    setTemplateSorgente([]);
    setSelezionati([]);
    setFiltroImport('');
    setImportErrore('');
    setImportEsito(null);
    try {
      // @api GET /api/report-templates/importa/societa -> {"success":true,"data":[{"id":"number","nome":"string","numTemplate":"number"}]}
      const r = await apiFetch<{ success: boolean; data: SocietaSorgente[] }>('/api/report-templates/importa/societa');
      setSorgenti(r.data || []);
    } catch (e: unknown) {
      setImportErrore(e instanceof Error ? e.message : 'Errore nel caricamento delle società');
    }
  };

  const cambiaSorgente = async (id: string) => {
    setSorgenteId(id);
    setSelezionati([]);
    setTemplateSorgente([]);
    setImportEsito(null);
    setImportErrore('');
    if (!id) return;
    setLoadingSorgente(true);
    try {
      // @api GET /api/report-templates/importa/templates?sorgenteId= -> {"success":true,"data":[{"id":"number","nome":"string","moduloGestionale":"string","attivo":"boolean","dimensioneHbs":"number","haSmtp":"boolean"}]}
      const r = await apiFetch<{ success: boolean; data: TemplateSorgente[] }>(
        `/api/report-templates/importa/templates?sorgenteId=${id}`
      );
      setTemplateSorgente(r.data || []);
    } catch (e: unknown) {
      setImportErrore(e instanceof Error ? e.message : 'Errore nel caricamento dei template');
    } finally {
      setLoadingSorgente(false);
    }
  };

  // Società selezionabili come sorgente: tutte quelle del gruppo tranne la destinazione
  const sorgentiDisponibili = sorgenti.filter(s => String(s.id) !== String(societaId));

  // Nome della società di destinazione — dalla lista utente, con fallback sulle sorgenti
  const nomeSocietaDestinazione =
    listaSocieta.find(s => String(s.id) === String(societaId))?.nome
    || sorgenti.find(s => String(s.id) === String(societaId))?.nome
    || '';

  const templateSorgenteFiltrati = templateSorgente.filter(t =>
    !filtroImport || t.nome.toLowerCase().includes(filtroImport.toLowerCase())
  );
  const tuttiSelezionati =
    templateSorgenteFiltrati.length > 0 &&
    templateSorgenteFiltrati.every(t => selezionati.includes(t.id));

  const toggleSelezione = (id: number) => {
    setSelezionati(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelezionaTutti = () => {
    const idsVisibili = templateSorgenteFiltrati.map(t => t.id);
    setSelezionati(prev => tuttiSelezionati
      ? prev.filter(id => !idsVisibili.includes(id))
      : prev.concat(idsVisibili.filter(id => !prev.includes(id)))
    );
  };

  const eseguiImport = async () => {
    if (!societaId || selezionati.length === 0) return;
    setImportando(true);
    setImportErrore('');
    try {
      // @api POST /api/report-templates/importa {"societaId":"number","templateIds":["number"]} -> {"success":true,"data":{"importati":[...],"totale":"number"}}
      const r = await apiFetch<{ success: boolean; data: { importati: EsitoImport[]; totale: number } }>(
        '/api/report-templates/importa',
        {
          method: 'POST',
          body: JSON.stringify({ societaId: parseInt(societaId), templateIds: selezionati }),
        }
      );
      setImportEsito(r.data.importati || []);
      setSelezionati([]);
      caricaLista();
    } catch (e: unknown) {
      setImportErrore(e instanceof Error ? e.message : 'Errore nell\'importazione');
    } finally {
      setImportando(false);
    }
  };

  const toggleAttivo = async (id: number) => {
    try {
      // @api PATCH /api/report-templates/:id/toggle-attivo -> {"success":true,"data":{"attivo":"boolean"}}
      await apiFetch(`/api/report-templates/${id}/toggle-attivo`, { method: 'PATCH' });
      caricaLista();
    } catch { /* errore */ }
  };





  return (
    <Layout>
      <div className="bop-page-header">
        <h1 className="bop-page-title">Report</h1>
        {canEdit && (
          <div className="bop-flex">
            <button
              className="bop-btn-secondary"
              onClick={apriImport}
              disabled={!societaId}
              title={societaId
                ? 'Copia template di stampa da un\'altra società del gruppo'
                : 'Seleziona prima la società di destinazione'}
            >
              ⤓ Importa da altra società
            </button>
            <button className="bop-btn-primary" onClick={apriCrea}>+ Nuovo template</button>
          </div>
        )}
      </div>

      <div className="bop-filter-bar">
        {!isDisabled && (
          <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
            {isSuperadmin && <option value="">— Seleziona società —</option>}
            {listaSocieta.map(s => (
              <option key={s.id} value={String(s.id)}>{s.nome}</option>
            ))}
          </select>
        )}
        <input className="bop-input" placeholder="Cerca per nome..." value={filtroTesto} onChange={e => setFiltroTesto(e.target.value)} />
        <select className="bop-select" value={filtroModulo} onChange={e => setFiltroModulo(e.target.value)}>
          <option value="">Tutti i moduli</option>
          {MODULI_REPORT.map(m => <option key={m} value={m}>{MODULO_REPORT_LABEL[m] || m}</option>)}
        </select>
        <select className="bop-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
          <option value="">Tutti gli stati</option>
          <option value="attivo">Attivo</option>
          <option value="inattivo">Inattivo</option>
        </select>
      </div>

      <div className="bop-text-muted">{filtered.length} template</div>

      <div className="bop-card">
        <table className="bop-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Modulo</th>
              <th>SMTP / Mittente</th>
              <th>Aggiornato</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {!pronta ? (
              <tr><td colSpan={6}><div className="bop-empty-state">Seleziona una società</div></td></tr>
            ) : loading ? (
              <tr><td colSpan={6}><div className="bop-empty-state">Caricamento...</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6}><div className="bop-empty-state">Nessun template trovato</div></td></tr>
            ) : (
              filtered.map(t => (
                <tr key={t.id}>
                  <td>
                    <span className="bop-text-primary">{t.nome}</span>
                    {t.descrizione && <div className="bop-text-muted">{t.descrizione}</div>}
                  </td>
                  <td><span className={`bop-badge ${BADGE_MODULO_REPORT[t.moduloGestionale] || 'bop-badge-neutral'}`}>{MODULO_REPORT_LABEL[t.moduloGestionale] || t.moduloGestionale}</span></td>
                  <td>
                    {t.smtp ? (
                      <>
                        <span className="bop-badge bop-badge-info">{t.smtp.tipo}</span>
                        <div className="bop-text-muted">
                          {t.mittenteNomeOverride || t.smtp.fromName} &lt;{t.mittenteEmailOverride || t.smtp.fromEmail}&gt;
                        </div>
                      </>
                    ) : (
                      <span className="bop-text-muted">—</span>
                    )}
                  </td>
                  <td className="bop-text-muted">{new Date(t.updatedAt).toLocaleDateString('it-IT')}</td>
                  <td>
                    <span className={`bop-badge ${t.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                      {t.attivo ? 'Attivo' : 'Inattivo'}
                    </span>
                  </td>
                  <td>
                    <div className="bop-flex">
                      {canEdit && (
                        <>
                          <button className="bop-btn-icon" title="Modifica parametri" onClick={() => apriModifica(t)}>⚙️</button>
                          <button className="bop-btn-icon" title="Apri editor" onClick={() => window.open(`/impostazioni/report/${t.id}/editor`, '_blank')}>✏️</button>
                          <button className="bop-btn-icon" title="Duplica" onClick={() => apriDuplica(t)}>📋</button>
                          <button className="bop-btn-icon" title={t.attivo ? 'Disattiva' : 'Attiva'} onClick={() => toggleAttivo(t.id)}>
                            {t.attivo ? '⊖' : '⊕'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modale Creazione/Modifica ── */}
      {modalOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--lg">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">{editId ? 'Modifica template' : 'Nuovo template'}</h2>
              <button className="bop-btn-icon" onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="bop-modal__body">
              {/* Sezione 1: Identificazione */}
              <div className="bop-section-label">Identificazione</div>
              <div className="bop-form-row">
                <div className="bop-form-group">
                  <label className="bop-label">Nome template <span className="req">*</span></label>
                  <input className="bop-input" value={nome} onChange={e => setNome(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Modulo gestionale <span className="req">*</span></label>
                  <select className="bop-select" value={moduloGestionale} onChange={e => setModuloGestionale(e.target.value)}>
                    {MODULI_REPORT.map(m => <option key={m} value={m}>{MODULO_REPORT_LABEL[m] || m}</option>)}
                  </select>
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Descrizione</label>
                <input className="bop-input" value={descrizione} onChange={e => setDescrizione(e.target.value)} />
              </div>

              <div className="bop-section-divider" />

              {/* Sezione 2: File template */}
              <div className="bop-section-label">File template</div>
              <div
                className={`bop-upload-zone ${hbsFile ? 'bop-upload-zone--success' : ''}`}
                onClick={() => hbsInputRef.current?.click()}
              >
                {hbsFile ? (
                  <div className="bop-upload-zone__file">📄 {hbsFile.name}</div>
                ) : editId ? (
                  <>
                    <div className="bop-upload-zone__title">File attuale: mantenuto</div>
                    <div className="bop-upload-zone__sub">Clicca per sostituire il file .hbs</div>
                  </>
                ) : (
                  <>
                    <div className="bop-upload-zone__title">Clicca per caricare il file .hbs</div>
                    <div className="bop-upload-zone__sub">File Handlebars (.hbs) obbligatorio</div>
                  </>
                )}
              </div>
              <input
                ref={hbsInputRef}
                type="file"
                accept=".hbs"
                style={{ display: 'none' }}
                onChange={e => { setHbsFile(e.target.files?.[0] || null); e.target.value = ''; }}
              />
              <div className="bop-form-group">
                <label className="bop-label">CSS Override</label>
                <textarea className="bop-input" rows={4} value={cssOverride} onChange={e => setCssOverride(e.target.value)} placeholder="CSS aggiuntivo opzionale" />
              </div>

              <div className="bop-section-divider" />

              {/* Sezione 3: Configurazione email */}
              <div className="bop-section-label">Configurazione email</div>
              <div className="bop-smtp-override-box">
                <div className="bop-smtp-override-box__title">SMTP e mittente</div>
                <div className="bop-form-group">
                  <label className="bop-label">SMTP</label>
                  <select className="bop-select" value={smtpId} onChange={e => setSmtpId(e.target.value)}>
                    <option value="">— Nessuno —</option>
                    {smtpOptions.map(s => (
                      <option key={s.id} value={s.id}>{s.fromName} &lt;{s.fromEmail}&gt; — {s.tipo}</option>
                    ))}
                  </select>
                </div>
                <div className="bop-smtp-override-info">I campi sottostanti sovrascrivono il mittente dell'SMTP selezionato (facoltativi)</div>
                <div className="bop-form-row">
                  <div className="bop-form-group">
                    <label className="bop-label">Nome mittente override</label>
                    <input className="bop-input" value={mittenteNomeOverride} onChange={e => setMittenteNomeOverride(e.target.value)} />
                  </div>
                  <div className="bop-form-group">
                    <label className="bop-label">Email mittente override</label>
                    <input className="bop-input" type="email" value={mittenteEmailOverride} onChange={e => setMittenteEmailOverride(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Oggetto email default</label>
                <input className="bop-input" value={oggettoEmailDefault} onChange={e => setOggettoEmailDefault(e.target.value)} placeholder='Es. Riepilogo commessa {{commessa.numero}}' />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Corpo email default</label>
                <textarea className="bop-input" rows={4} value={corpoEmailDefault} onChange={e => setCorpoEmailDefault(e.target.value)} />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setModalOpen(false)}>Annulla</button>
              <button className="bop-btn-primary" onClick={salvaTemplate} disabled={saving || !nome.trim() || (!editId && !hbsFile)}>
                {saving ? 'Salvataggio...' : editId ? 'Salva modifiche' : 'Crea template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale Duplica ── */}
      {duplicaModalOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--sm">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Duplica template</h2>
              <button className="bop-btn-icon" onClick={() => setDuplicaModalOpen(false)}>✕</button>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Nome nuovo template <span className="req">*</span></label>
                <input
                  className="bop-input"
                  value={duplicaNome}
                  onChange={e => setDuplicaNome(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setDuplicaModalOpen(false)}>Annulla</button>
              <button className="bop-btn-primary" onClick={eseguiDuplica} disabled={duplicando || !duplicaNome.trim()}>
                {duplicando ? 'Duplicazione...' : 'Duplica'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modale Importa da altra società ── */}
      {importOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--xl">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Importa template da altra società</h2>
              <button className="bop-btn-icon" onClick={() => setImportOpen(false)}>✕</button>
            </div>
            <div className="bop-modal__body">
              {importErrore && <div className="bop-alert bop-alert--danger">{importErrore}</div>}

              {importEsito ? (
                <>
                  <div className="bop-alert bop-alert--success">
                    {importEsito.length === 1
                      ? '1 template importato in '
                      : `${importEsito.length} template importati in `}
                    <strong>{nomeSocietaDestinazione}</strong>. Sono già attivi.
                  </div>
                  <div className="bop-import-report__esito">
                    {importEsito.map(e => (
                      <div key={e.id} className="bop-import-report__esito-riga">
                        <span className="bop-text-primary">{e.nome}</span>
                        {e.rinominato && (
                          <span className="bop-text-muted">
                            rinominato — nella società esisteva già «{e.nomeOriginale}»
                          </span>
                        )}
                        {e.smtpDaConfigurare && (
                          <span className="bop-badge bop-badge-warning">SMTP da riconfigurare</span>
                        )}
                        <button
                          className="bop-btn-secondary bop-btn-sm"
                          onClick={() => window.open(`/impostazioni/report/${e.id}/editor`, '_blank')}
                        >
                          Apri editor
                        </button>
                      </div>
                    ))}
                  </div>
                  {importEsito.some(e => e.smtpDaConfigurare) && (
                    <div className="bop-import-report__nota">
                      La configurazione SMTP non viene copiata: appartiene alla società di
                      origine. Per i template segnalati vanno riselezionati SMTP e mittente
                      dalle impostazioni del template.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="bop-import-report__intro">
                    I template vengono <strong>copiati</strong> in{' '}
                    <strong>{nomeSocietaDestinazione || '—'}</strong>: struttura Handlebars, CSS,
                    dati campione, oggetto e corpo email. L&apos;SMTP non viene copiato perché
                    legato alla società di origine. Gli originali restano invariati.
                  </div>

                  <div className="bop-import-report__toolbar">
                    <select
                      className="bop-select"
                      value={sorgenteId}
                      onChange={e => cambiaSorgente(e.target.value)}
                    >
                      <option value="">— Società di origine —</option>
                      {sorgentiDisponibili.map(s => (
                        <option key={s.id} value={String(s.id)}>
                          {s.nome} ({s.numTemplate} template)
                        </option>
                      ))}
                    </select>
                    <input
                      className="bop-input"
                      placeholder="Cerca per nome..."
                      value={filtroImport}
                      onChange={e => setFiltroImport(e.target.value)}
                      disabled={!sorgenteId}
                    />
                  </div>

                  <div className="bop-import-report__lista">
                    <table className="bop-table">
                      <thead>
                        <tr>
                          <th className="bop-table__th--select">
                            <input
                              type="checkbox"
                              checked={tuttiSelezionati}
                              onChange={toggleSelezionaTutti}
                              disabled={templateSorgenteFiltrati.length === 0}
                            />
                          </th>
                          <th>Nome</th>
                          <th>Modulo</th>
                          <th>Stato</th>
                          <th>Aggiornato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!sorgenteId ? (
                          <tr><td colSpan={5}><div className="bop-empty-state">
                            {sorgentiDisponibili.length === 0
                              ? 'Nessun\'altra società del gruppo ha template di stampa'
                              : 'Seleziona la società di origine'}
                          </div></td></tr>
                        ) : loadingSorgente ? (
                          <tr><td colSpan={5}><div className="bop-empty-state">Caricamento...</div></td></tr>
                        ) : templateSorgenteFiltrati.length === 0 ? (
                          <tr><td colSpan={5}><div className="bop-empty-state">Nessun template trovato</div></td></tr>
                        ) : (
                          templateSorgenteFiltrati.map(t => (
                            <tr key={t.id} className="bop-import-report__riga" onClick={() => toggleSelezione(t.id)}>
                              <td className="bop-table__td--select">
                                <input
                                  type="checkbox"
                                  checked={selezionati.includes(t.id)}
                                  onChange={() => toggleSelezione(t.id)}
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                              <td>
                                <span className="bop-text-primary">{t.nome}</span>
                                {t.descrizione && <div className="bop-text-muted">{t.descrizione}</div>}
                              </td>
                              <td>
                                <span className={`bop-badge ${BADGE_MODULO_REPORT[t.moduloGestionale] || 'bop-badge-neutral'}`}>
                                  {MODULO_REPORT_LABEL[t.moduloGestionale] || t.moduloGestionale}
                                </span>
                              </td>
                              <td>
                                <span className={`bop-badge ${t.attivo ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                                  {t.attivo ? 'Attivo' : 'Inattivo'}
                                </span>
                              </td>
                              <td className="bop-text-muted">{new Date(t.updatedAt).toLocaleDateString('it-IT')}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="bop-modal__footer">
              {importEsito ? (
                <button className="bop-btn-primary" onClick={() => setImportOpen(false)}>Chiudi</button>
              ) : (
                <>
                  <span className="bop-text-muted bop-import-report__conteggio">
                    {selezionati.length} selezionat{selezionati.length === 1 ? 'o' : 'i'}
                  </span>
                  <button className="bop-btn-secondary" onClick={() => setImportOpen(false)}>Annulla</button>
                  <button
                    className="bop-btn-primary"
                    onClick={eseguiImport}
                    disabled={importando || selezionati.length === 0}
                  >
                    {importando ? 'Importazione...' : `Importa ${selezionati.length || ''}`.trim()}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
